import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAcademy } from "../AcademyContext";
import { ACADEMY_PAGE_ORDER, ACADEMY_PAGES } from "../data/academyContent";
import {
  CRM_FULL_CYCLE_WORKFLOW,
  TABLES_WORKFLOW,
  TASK_PLANNING_WORKFLOW,
} from "../data/crmFullCycleWorkflow";
import {
  MARKETING_BIRTHDAY_WORKFLOW,
  MARKETING_GLOBAL_WORKFLOW,
} from "../data/marketingWorkflows";

const WORKFLOW_MODULES = [
  {
    workflow: CRM_FULL_CYCLE_WORKFLOW,
    icon: "01",
    routeLabel: "academy_module_route_full_cycle",
    outcome: "academy_module_outcome_full_cycle",
  },
  {
    workflow: TASK_PLANNING_WORKFLOW,
    icon: "02",
    routeLabel: "academy_module_route_task_planning",
    outcome: "academy_module_outcome_task_planning",
  },
  {
    workflow: TABLES_WORKFLOW,
    icon: "03",
    routeLabel: "academy_module_route_tables",
    outcome: "academy_module_outcome_tables",
  },
];

const MARKETING_WORKFLOW_MODULES = [
  {
    workflow: MARKETING_GLOBAL_WORKFLOW,
    icon: "A",
    routeLabel: "academy_marketing_global_workflow_path",
    outcome: "academy_marketing_global_workflow_outcome",
  },
  {
    workflow: MARKETING_BIRTHDAY_WORKFLOW,
    icon: "B",
    routeLabel: "academy_marketing_birthday_workflow_path",
    outcome: "academy_marketing_birthday_workflow_outcome",
  },
];

export default function AcademyPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [resetNotice, setResetNotice] = useState("");
  const [resettingProgress, setResettingProgress] = useState(false);
  const [resetError, setResetError] = useState("");
  const {
    academyEnabled,
    completed,
    autoStart,
    startTour,
    markLessonComplete,
    resetProgress,
    setAutoStart,
    workflowState,
    trainingSession,
    startFullCycleWorkflow,
    startTaskPlanningWorkflow,
    startTablesWorkflow,
    startMarketingWorkflow,
    taskPlanningStarting,
    taskPlanningStartError,
    openFinalCleanup,
  } = useAcademy();

  const lessons = ACADEMY_PAGE_ORDER.map((key) => ACADEMY_PAGES[key]);
  const completedCount = lessons.filter((lesson) => completed[lesson.key]).length;
  const lessonProgress = useMemo(
    () =>
      lessons.length
        ? Math.round((completedCount / lessons.length) * 100)
        : 0,
    [completedCount, lessons.length],
  );
  const completedModules = WORKFLOW_MODULES.filter(({ workflow }) =>
    Boolean(completed[workflow.key]),
  ).length;
  const allModulesComplete = completedModules === WORKFLOW_MODULES.length;
  const completedMarketingModules = MARKETING_WORKFLOW_MODULES.filter(
    ({ workflow }) => Boolean(completed[workflow.key]),
  ).length;

  useEffect(() => {
    if (!resetNotice) return undefined;

    const timer = window.setTimeout(() => setResetNotice(""), 3500);
    return () => window.clearTimeout(timer);
  }, [resetNotice]);

  if (!academyEnabled) {
    return null;
  }

  const handleResetProgress = async () => {
    const confirmed = window.confirm(
      t("academy_reset_progress_confirm"),
    );
    if (!confirmed) return;

    setResettingProgress(true);
    setResetError("");
    try {
      await resetProgress();
      setResetNotice(
        t("academy_progress_reset_success"),
      );
    } catch (error) {
      setResetError(
        error?.response?.data?.detail ||
          t("academy_progress_reset_error"),
      );
    } finally {
      setResettingProgress(false);
    }
  };

  const startModule = (key) => {
    if (key === CRM_FULL_CYCLE_WORKFLOW.key) {
      startFullCycleWorkflow();
      return;
    }
    if (key === TASK_PLANNING_WORKFLOW.key) {
      startTaskPlanningWorkflow(trainingSession?.sample || null);
      return;
    }
    startTablesWorkflow();
  };

  return (
    <div className="academyPage">
      <header className="academyPageHero">
        <div>
          <span className="academyPageEyebrow">{t("academy_name")}</span>
          <h1>{t("academy_page_heading")}</h1>
          <p>{t("academy_page_intro")}</p>
        </div>

        <div className="academyOverallProgress">
          <strong>{Math.round((completedModules / 3) * 100)}%</strong>
          <span>
            {t("academy_completed_modules", {
              completed: completedModules,
              total: 3,
            })}
          </span>
          <div>
            <i style={{ width: `${(completedModules / 3) * 100}%` }} />
          </div>
        </div>
      </header>

      <section
        className="academyLearningPath"
        aria-label={t("academy_learning_path")}
      >
        <div className="academyLearningPathHeader">
          <div>
            <span className="academyPageEyebrow">
              {t("academy_practical_route")}
            </span>
            <h2>{t("academy_continue_when_ready")}</h2>
            <p>{t("academy_continue_when_ready_description")}</p>
          </div>
          <span className="academyLearningPathState">
            {allModulesComplete
              ? t("academy_route_complete")
              : t("academy_next_module", {
                  number: completedModules + 1,
                })}
          </span>
        </div>

        <div className="academyModuleGrid">
          {WORKFLOW_MODULES.map(({ workflow, icon, routeLabel, outcome }, index) => {
            const done = Boolean(completed[workflow.key]);
            const unlocked =
              index === 0 ||
              Boolean(completed[WORKFLOW_MODULES[index - 1].workflow.key]);
            const active = workflowState?.key === workflow.key;

            return (
              <article
                key={workflow.key}
                className={`academyModuleCard ${done ? "is-complete" : ""} ${
                  active ? "is-active" : ""
                } ${!unlocked ? "is-locked" : ""}`}
              >
                <div className="academyModuleCardTop">
                  <span>{done ? "✓" : icon}</span>
                  <small>
                    {t("academy_module_progress", {
                      current: index + 1,
                      total: WORKFLOW_MODULES.length,
                    })}
                  </small>
                </div>
                <h3>{t(workflow.title)}</h3>
                <p>{t(workflow.description)}</p>
                <div className="academyModuleRoute">{t(routeLabel)}</div>
                <ul>
                  <li>{t(outcome)}</li>
                  <li>
                    ◷{" "}
                    {t("academy_about_minutes", {
                      count: workflow.estimatedMinutes,
                    })}
                  </li>
                  <li>{t("academy_data_preserved_between_modules")}</li>
                </ul>
                <button
                  type="button"
                  disabled={!unlocked || taskPlanningStarting}
                  onClick={() => startModule(workflow.key)}
                >
                  {!unlocked
                    ? t("academy_finish_previous_first")
                    : active
                      ? t("academy_continue_module")
                      : done
                        ? t("academy_repeat_module")
                        : index === 0
                          ? t("academy_start_tutorial")
                          : t("academy_continue_training")}{" "}
                  {unlocked ? "→" : ""}
                </button>
              </article>
            );
          })}
        </div>

        {taskPlanningStartError && (
          <div className="academyModuleError">{taskPlanningStartError}</div>
        )}
      </section>

      <section
        className="academyLearningPath"
        aria-label={t("academy_marketing_practice_title")}
      >
        <div className="academyLearningPathHeader">
          <div>
            <span className="academyPageEyebrow">
              {t("academy_marketing_practice_eyebrow")}
            </span>
            <h2>{t("academy_marketing_practice_title")}</h2>
            <p>{t("academy_marketing_practice_description")}</p>
          </div>
          <span className="academyLearningPathState">
            {t("academy_completed_modules", {
              completed: completedMarketingModules,
              total: MARKETING_WORKFLOW_MODULES.length,
            })}
          </span>
        </div>

        <div className="academyModuleGrid">
          {MARKETING_WORKFLOW_MODULES.map(
            ({ workflow, icon, routeLabel, outcome }, index) => {
              const done = Boolean(completed[workflow.key]);
              const active = workflowState?.key === workflow.key;

              return (
                <article
                  key={workflow.key}
                  className={`academyModuleCard ${
                    done ? "is-complete" : ""
                  } ${active ? "is-active" : ""}`}
                >
                  <div className="academyModuleCardTop">
                    <span>{done ? "✓" : icon}</span>
                    <small>
                      {t("academy_marketing_scenario_number", {
                        current: index + 1,
                        total: MARKETING_WORKFLOW_MODULES.length,
                      })}
                    </small>
                  </div>
                  <h3>{t(workflow.title)}</h3>
                  <p>{t(workflow.description)}</p>
                  <div className="academyModuleRoute">{t(routeLabel)}</div>
                  <ul>
                    <li>{t(outcome)}</li>
                    <li>
                      ◷{" "}
                      {t("academy_about_minutes", {
                        count: workflow.estimatedMinutes,
                      })}
                    </li>
                    <li>{t("academy_marketing_no_auto_send")}</li>
                  </ul>
                  <button
                    type="button"
                    onClick={() => startMarketingWorkflow(workflow.key)}
                  >
                    {active
                      ? t("academy_continue_module")
                      : done
                        ? t("academy_repeat_module")
                        : t("academy_marketing_start_scenario")}{" "}
                    →
                  </button>
                </article>
              );
            },
          )}
        </div>
      </section>

      {allModulesComplete && trainingSession?.sample && (
        <section className="academyFinale">
          <div className="academyFinaleIcon">✓</div>
          <div>
            <span className="academyPageEyebrow">
              {t("academy_training_complete")}
            </span>
            <h2>{t("academy_full_process_complete")}</h2>
            <p>{t("academy_full_process_complete_description")}</p>
          </div>
          <div className="academyFinaleActions">
            <button type="button" onClick={() => navigate("/menu/sales/contacts")}>
              {t("academy_keep_and_review")}
            </button>
            <button type="button" onClick={openFinalCleanup}>
              {t("academy_finish_and_choose_cleanup")} →
            </button>
          </div>
        </section>
      )}

      <section className="academyPreferences">
        <label>
          <input
            type="checkbox"
            checked={autoStart}
            onChange={(event) => setAutoStart(event.target.checked)}
          />
          <span>
            <strong>{t("academy_auto_offer_help")}</strong>
            <small>{t("academy_auto_offer_help_description")}</small>
          </span>
        </label>

        <div className="academyPreferencesActions">
          {resetNotice && (
            <span className="academyResetStatus" role="status" aria-live="polite">
              ✓ {resetNotice}
            </span>
          )}
          {resetError && (
            <span
              className="academyResetStatus is-error"
              role="alert"
            >
              {resetError}
            </span>
          )}
          <button
            type="button"
            onClick={handleResetProgress}
            disabled={resettingProgress}
          >
            {resettingProgress
              ? t("academy_resetting_progress")
              : t("academy_reset_all_progress")}
          </button>
        </div>
      </section>

      <div className="academyReferenceHeading">
        <div>
          <span className="academyPageEyebrow">
            {t("academy_interface_reference")}
          </span>
          <h2>{t("academy_short_page_tours")}</h2>
        </div>
        <span>
          {t("academy_percent_studied", { percent: lessonProgress })}
        </span>
      </div>

      <div className="academyLessonGrid">
        {lessons.map((lesson, index) => {
          const isComplete = Boolean(completed[lesson.key]);
          const interactive = Boolean(lesson.steps?.length);

          return (
            <article
              key={lesson.key}
              className={`academyLessonCard ${isComplete ? "is-complete" : ""}`}
            >
              <div className="academyLessonCardTop">
                <span className="academyLessonIcon">{lesson.icon}</span>
                <span className="academyLessonIndex">
                  {String(index + 1).padStart(2, "0")}
                </span>
              </div>

              <div className="academyLessonContent">
                <small>{t(lesson.subtitle)}</small>
                <h2>{t(lesson.title)}</h2>
                <p>{t(lesson.description)}</p>
              </div>

              <div className="academyLessonMeta">
                <span>
                  ◷{" "}
                  {t("academy_minutes_short", {
                    count: lesson.estimatedMinutes || 3,
                  })}
                </span>
                <span>
                  {interactive
                    ? t("academy_interactive_tour")
                    : t("academy_guide")}
                </span>
              </div>

              <div className="academyLessonActions">
                <button
                  type="button"
                  className="academyLessonSecondary"
                  onClick={() => markLessonComplete(lesson.key, !isComplete)}
                >
                  {isComplete
                    ? t("academy_studied_complete")
                    : t("academy_mark_studied")}
                </button>

                <button
                  type="button"
                  className="academyLessonPrimary"
                  onClick={() => {
                    if (interactive) {
                      startTour(lesson.key);
                    } else {
                      navigate(lesson.route);
                      window.setTimeout(
                        () =>
                          window.dispatchEvent(
                            new CustomEvent("academy-open-help", {
                              detail: lesson.key,
                            }),
                          ),
                        400,
                      );
                    }
                  }}
                >
                  {interactive
                    ? t("academy_start")
                    : t("academy_open_page")}{" "}
                  →
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
